import { ComponentEntity, Entity, ResourceEntity } from '@backstage/catalog-model';
import { ScorecardEntity } from './scorecard';
import { RestEndpointMethodTypes } from '@octokit/rest';
import { createHash } from 'crypto';
import { ArgusApp, ArgusCluster, ArgusStackDependency } from '@eng-portal/plugin-argus-common';

export const AWS_ACCOUNT_RESOURCE_TYPE = 'aws-account';

export type AWSAccountResourceEntity = ResourceEntity & ScorecardEntity & {
  spec: {
    type: typeof AWS_ACCOUNT_RESOURCE_TYPE
    id: string
    name: string
    nameAlias?: string
    initiative: string
    team: string
    project: string
    company: string
    pocSupport?: string
    emailSupport?: string
    slackSupport?: string
    slackOncall?: string
  }
};

export function getAccountResourceID(accountID: string): string {
  return `${AWS_ACCOUNT_RESOURCE_TYPE}-${accountID}`;
}

export const GITHUB_ORGANIZATION_RESOURCE_TYPE = 'github-org';
export const GITHUB_REPOSITORY_RESOURCE_TYPE = 'github-repository';

export type GitHubOrganizationResourceEntity = ResourceEntity & {
  spec: {
    type: typeof GITHUB_ORGANIZATION_RESOURCE_TYPE
    owner: string
    orgOwners: string[]
  }
};

export type GitHubContributor = {
  login: string
  contributions: number
  avatarUrl: string
  htmlUrl: string
  email?: string
};

export type GitHubVulnerabilityBreakdown = {
  dependabot?: number
  codeScanning?: number
  secretScanning?: number
};

export type GitHubVulnerability = {
  severity: 'critical' | 'high' | 'medium' | 'low'
  count: number
  breakdown?: GitHubVulnerabilityBreakdown
};

export type GitHubRepositoryResourceEntity = ResourceEntity & ScorecardEntity & {
  spec: {
    type: typeof GITHUB_REPOSITORY_RESOURCE_TYPE
    owner: string
    repo: string
    fullName: string
    description: string | null
    url: string
    htmlUrl: string
    defaultBranch: string
    isFork: boolean
    isArchived?: boolean
    createdAt: string
    pushedAt: string
    branchProtection: RestEndpointMethodTypes["repos"]["getBranchProtection"]["response"]["data"] | null
    topContributors?: GitHubContributor[]
    securityVulnerabilities?: GitHubVulnerability[]
    cziOwners?: string[]
    /** GitHub logins of the CZI owners */
    cziOwnerLogins?: string[]
    codeownersContent?: string | null
    /** Raw GitHub teams/users from CODEOWNERS file assigned to * (global owners) */
    codeownersGlobalOwners?: string[]
    repoAdmins?: string[]
  }
};
export function getGitHubRepositoryResourceID(org: string, repo: string): string {
  return `${GITHUB_REPOSITORY_RESOURCE_TYPE}-${org}-${repo}`;
}

export const GITHUB_ACTION_RESOURCE_TYPE = 'github-action';
export type ActionMetadata = {
  org: string
  repo: string
  path?: string
  version: string
};
export type GitHubActionResourceEntity = ResourceEntity & {
  spec: ActionMetadata & {
    type: typeof GITHUB_ACTION_RESOURCE_TYPE
    usedByUnarchivedRepos: boolean
  },
};
export function getGitHubActionResourceID(actionMetadata: ActionMetadata): string {
  const { org, repo, version, path } = actionMetadata;
  const pathSha = path ? createHash('sha1').update(path).digest('hex').slice(0, 7) : '';
  return `${GITHUB_ACTION_RESOURCE_TYPE}-${org}-${repo}-${path ? pathSha + '-' : ''}${version}`;
}

export const EKS_CLUSTER_RESOURCE_TYPE = 'eks-cluster';

export type EKSClusterResourceEntity = ResourceEntity & ScorecardEntity & {
  spec: {
    type: typeof EKS_CLUSTER_RESOURCE_TYPE
    awsRegion: string
    version: string
    versionEOL: string
    endpoint: string
    tags: Record<string, string>
    argusCluster: ArgusCluster | undefined
    readerTeams: string[]
  }
};

export function getClusterResourceID(clusterName: string, accountID: string): string {
  return `${EKS_CLUSTER_RESOURCE_TYPE}-${accountID}-${clusterName}`;
}

export const CZI_ANNOTATION_ARGUS_CLUSTER = 'czi.team/argus-cluster';
export const isArgusCluster = (entity: Entity): boolean => entity?.metadata.annotations?.[CZI_ANNOTATION_ARGUS_CLUSTER] === 'true';

export const ROUTE53_ZONE_RESOURCE_TYPE = 'route53-zone';

export type Route53ZoneResourceEntity = ResourceEntity & {
  spec: {
    type: typeof ROUTE53_ZONE_RESOURCE_TYPE
    zoneId: string
    zoneName: string
    isPrivate: boolean
    recordSetCount: number
    comment?: string
  }
};

export function getRoute53ZoneResourceID(zoneId: string, accountID: string): string {
  return `${ROUTE53_ZONE_RESOURCE_TYPE}-${accountID}-${zoneId}`;
}

export const RDS_CLUSTER_RESOURCE_TYPE = 'rds-cluster';

export type RdsRecommendation = {
  recommendationId: string
  typeId?: string
  severity?: string
  status?: string
  detection?: string
  recommendation?: string
  description?: string
  category?: string
};

export type RdsClusterResourceEntity = ResourceEntity & ScorecardEntity & {
  spec: {
    type: typeof RDS_CLUSTER_RESOURCE_TYPE
    dbClusterIdentifier: string
    dbClusterResourceId: string
    engine: string
    engineVersion: string
    status: string
    awsRegion: string
    endpoint?: string
    readerEndpoint?: string
    multiAZ: boolean
    storageEncrypted: boolean
    deletionProtection: boolean
    awsRecommendations: RdsRecommendation[]
  }
};

export function getRdsClusterResourceID(dbClusterIdentifier: string, accountID: string): string {
  return `${RDS_CLUSTER_RESOURCE_TYPE}-${accountID}-${dbClusterIdentifier}`;
}

export const ARGUS_APP_COMPONENT_TYPE = 'argus-app';
export const isArgusAppComponent = (entity: Entity): boolean => entity?.spec?.type === ARGUS_APP_COMPONENT_TYPE;
export type ArgusAppComponentEntity = ComponentEntity & ScorecardEntity & {
  spec: {
    type: typeof ARGUS_APP_COMPONENT_TYPE
    app: ArgusApp
    helmDependencies?: ArgusStackDependency[]
  }
};
export function getArgusAppComponentID(appName: string): string {
  return `${ARGUS_APP_COMPONENT_TYPE}-${appName}`;
}
export function getGrafanaURL(cluster: ArgusCluster): string {
  return new URL(`https://${cluster.metrics_subdomain}.${cluster.base_domain}`).toString();
}
